import "./TagifyJobs.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTechs } from "../../core/Global";
import Icon from "../base/Icon";
import Avatar from "../base/Avatar";
import Technician from "../../modules/technicians/models/technician";

/**
 * TagifyTechs — native technician picker (no @yaireo/tagify, no dspro.js).
 *
 * - Suggestions come entirely from globalState active techs (useTechs — which
 *   already excludes suspended). No server request at all.
 * - Single and multi select, same UX/behaviour as TagifyJobs.
 */

interface TechSuggestion {
    id: string;
    name?: string;
    avatar?: string;
    phone?: string;
}

interface IProps {
    id: string;
    name?: string;
    label?: string;
    labelUp?: boolean;
    block?: boolean;
    placeholder?: string;
    multiple?: boolean;
    selectedId?: string;
    selectedIds?: string[];
    onSelect?: (techId?: string, tech?: TechSuggestion) => void;
    onRemove?: (techId?: string) => void;
    onChange?: (ids: string[], techs: TechSuggestion[]) => void;
}

const fromTech = (t: Technician): TechSuggestion => ({
    id: t.id,
    name: t.fullName,
    avatar: t.avatar,
    phone: t.phoneItem?.phoneFriendly ?? t.phoneItem?.phone ?? t.phoneRaw
});

export default function TagifyTechs(props: IProps) {

    const techs = useTechs();

    const all = useMemo<TechSuggestion[]>(() => (techs ?? []).map(fromTech), [techs]);

    const controlRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const preloadRef = useRef<string>("");
    const selectingRef = useRef<boolean>(false);

    const [query, setQuery] = useState<string>("");
    const [open, setOpen] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<TechSuggestion[]>([]);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [rect, setRect] = useState<{ top: number; left: number; width: number }>();

    const isSelected = (id: string) => selectedItems.some(x => x.id === id);
    const emit = (list: TechSuggestion[]) => props.onChange?.(list.map(x => x.id), list);

    // Local filtering + remove already-selected from the list.
    const visibleSuggestions = useMemo(() => {
        const q = query.trim().toLowerCase();
        return all.filter(t =>
            !isSelected(t.id) &&
            (q === "" ||
                (t.name?.toLowerCase().includes(q) ?? false) ||
                (t.phone?.toLowerCase().includes(q) ?? false))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [all, query, selectedItems]);

    // ----- preselect -----
    useEffect(() => {
        const ids = props.multiple ? (props.selectedIds ?? []) : (props.selectedId ? [props.selectedId] : []);
        const key = ids.join(",");
        if (key === preloadRef.current) return;
        preloadRef.current = key;
        setSelectedItems(all.filter(t => ids.includes(t.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedId, props.selectedIds, all]);

    // ----- dropdown positioning -----
    const updateRect = () => {
        const el = controlRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };

    useEffect(() => {
        if (!open) return;
        updateRect();
        const handler = () => updateRect();
        window.addEventListener("scroll", handler, true);
        window.addEventListener("resize", handler);
        return () => {
            window.removeEventListener("scroll", handler, true);
            window.removeEventListener("resize", handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedItems.length]);

    // ----- outside click -----
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (selectingRef.current) { selectingRef.current = false; return; }
            const t = e.target as Node;
            if (controlRef.current?.contains(t)) return;
            if (dropdownRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const openDropdown = () => {
        setOpen(true);
        setActiveIndex(-1);
    };

    const select = (s: TechSuggestion) => {
        if (props.multiple) {
            if (isSelected(s.id)) { remove(s); return; }
            const next = [...selectedItems, s];
            setSelectedItems(next);
            emit(next);
            props.onSelect?.(s.id, s);
            setOpen(true);
            setActiveIndex(-1);
            setTimeout(() => inputRef.current?.focus(), 0);
        } else {
            setSelectedItems([s]);
            emit([s]);
            props.onSelect?.(s.id, s);
            setQuery("");
            setOpen(false);
        }
    };

    const remove = (s: TechSuggestion) => {
        const next = selectedItems.filter(x => x.id !== s.id);
        setSelectedItems(next);
        emit(next);
        props.onRemove?.(s.id);
        if (!props.multiple) props.onSelect?.(undefined, undefined);
    };

    const clearSingle = () => {
        if (selectedItems[0]) remove(selectedItems[0]);
        setQuery("");
        setTimeout(() => { inputRef.current?.focus(); openDropdown(); }, 0);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) { openDropdown(); return; }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, visibleSuggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && visibleSuggestions[activeIndex]) select(visibleSuggestions[activeIndex]);
        } else if (e.key === "Escape") {
            setOpen(false);
        } else if (e.key === "Backspace" && query === "" && props.multiple && selectedItems.length > 0) {
            remove(selectedItems[selectedItems.length - 1]);
        }
    };

    useEffect(() => {
        if (activeIndex < 0 || !dropdownRef.current) return;
        const el = dropdownRef.current.querySelectorAll(".tj-suggestion")[activeIndex] as HTMLElement;
        el?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    // ----- render -----

    const SearchInput = (
        <input ref={inputRef} id={props.id} type="text" autoComplete="off"
            className="tj-input border-0 p-0"
            style={{ outline: "none", background: "transparent" }}
            placeholder={selectedItems.length === 0 ? (props.placeholder ?? "Select a technician") : ""}
            value={query}
            onFocus={openDropdown}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onKeyDown={onKeyDown} />
    );

    const Pill = (s: TechSuggestion) => (
        <span key={s.id} className="tj-tag">
            <Avatar avatarUrl={s.avatar} fullName={s.name} width={18} textSize={9} className="me-2" />
            <span className="text-truncate">{s.name}</span>
            <span className="tj-tag-x" onClick={e => { e.stopPropagation(); remove(s); }}>
                <Icon icon="xmark" size={11} />
            </span>
        </span>
    );

    const SuggestionRow = (s: TechSuggestion, i: number) => (
        <div key={s.id || i}
            className={"tj-suggestion" + (i === activeIndex ? " tj-active" : "")}
            onMouseEnter={() => setActiveIndex(i)}
            onMouseDown={e => {
                e.preventDefault();
                selectingRef.current = true;
                select(s);
                setTimeout(() => { selectingRef.current = false; }, 0);
            }}>
            <div className="d-flex align-items-center w-100">
                <Avatar avatarUrl={s.avatar} fullName={s.name} width={34} className="me-3" />
                <div className="d-flex flex-column">
                    <span className="fw-bold text-gray-900">{s.name}</span>
                    {
                        s.phone &&
                        <span className="d-flex align-items-center fs-8 text-gray-500 mt-1">
                            <Icon icon="phone" type="solid" className="me-1 text-gray-400" size={10} />
                            {s.phone}
                        </span>
                    }
                </div>
            </div>
        </div>
    );

    const showInput = props.multiple || selectedItems.length === 0;

    return (
        <div className="row align-items-center mb-10">
            {
                props.label &&
                <label className={"col col-12 " + (props.labelUp ? "mb-1" : (props.block ? "col-md-3" : "col-md-2")) + " fs-7"} htmlFor={props.id}>
                    {props.label}
                    <span id={(props.name ?? props.id) + "-err"} className="small text-danger mx-3 form-error" data-tagify="yes"></span>
                </label>
            }

            <div className={"col col-12 " + (props.labelUp ? "" : (props.block ? "col-md-9" : "col-md-6"))}>

                <div ref={controlRef}
                    className="form-control d-flex align-items-center tj-control"
                    onClick={() => { inputRef.current?.focus(); openDropdown(); }}>

                    {props.multiple && selectedItems.map(s => Pill(s))}

                    {
                        !props.multiple && selectedItems.length > 0 &&
                        <div className="tj-pill d-flex align-items-center w-100">
                            <Avatar avatarUrl={selectedItems[0].avatar} fullName={selectedItems[0].name} width={22} textSize={10} className="me-2" />
                            <span className="text-truncate">{selectedItems[0].name}</span>
                            <span className="tj-clear ms-auto ps-2" onClick={e => { e.stopPropagation(); clearSingle(); }}>
                                <Icon icon="xmark" />
                            </span>
                        </div>
                    }

                    {showInput && SearchInput}
                </div>

                <input id={props.id + "-val"} type="hidden" name={props.name}
                    value={selectedItems.map(x => x.id).join(",")} readOnly />
            </div>

            {
                open && rect &&
                createPortal(
                    <div ref={dropdownRef} className="tj-dropdown"
                        style={{ top: rect.top, left: rect.left, width: rect.width }}>
                        {
                            visibleSuggestions.length > 0 ?
                                visibleSuggestions.map((s, i) => SuggestionRow(s, i))
                                :
                                <div className="tj-empty">No technicians found</div>
                        }
                    </div>,
                    document.body
                )
            }
        </div>
    );
}
